import * as THREE from 'three';
import { WATER_FOG, noFog, type FogUniforms } from '../skin';
import { hash3 } from '../fishSwim';

/**
 * The Reef's light shadows (M246, the v6 brief's section 3, "Implement Lightweight Shadows for All Relevant Objects"):
 * no shadow map, a soft pixelated blob laid on the ground under each thing that casts one, all of a kind in one
 * instanced draw. Each blob is a quad on the ground's plane where it lies, its shape one of `SHAPES` irregular blobs
 * (the brief's "a broader irregular shadow footprint rather than a tiny circular dot"), as long and as wide as its
 * caster (a swimmer's spread wider, M253), turned the way it heads, slid away from the sun by its height over the ground as the light falls through the
 * water (the sun's way bent at the surface, `REFRACTION`), and darkest at its middle, where it meets its caster's foot.
 * It is no color of its own: the ground under it is taken toward the shade's tint (`SHADOWS.tint`, the brief's
 * "Naturally blue-gray or muted dark teal underwater", deeper at depth) by its alpha, so it never reads as black. Its
 * strength follows the sun's beam (none by night: M256), fades with the water's veil and with depth, and
 * past `SHADOWS.farM` from the camera; a swimming caster's fades and shrinks as it rises (`SHADOWS.creature`, the user's
 * word: "understated shadows that become progressively smaller or lighter as the animal swims away from the bottom").
 */

/** Light's index of refraction into seawater (about 1.34 at 20°C and 35 PSU in visible light; 1.333 for fresh water). */
const REFRACTION = 1.34;

/**
 * The shadows' settings (the brief's "Provide tunable settings"), each JUDGED by eye on a phone and a computer at the
 * Reef's home view:
 * - `alpha`: how far the ground is taken toward the tint at a blob's darkest, full sun, at the surface; `deepFade` of it
 *   lost by 30 m (the brief's "Less pronounced at greater water depth": more of the light there comes from all round).
 * - `tint`: the shade's color shallow, at `tintAtM[0]`, and deep, at `tintAtM[1]` (the brief's #526C70 and #304E60,
 *   its mid-depth #405D68 between them).
 * - `slideMost`: the most a blob slides from under its caster, as a share of its height (the light's slant clamped, so
 *   a low sun leaves no long streak).
 * - `farM`: how far from the camera the blobs are drawn, faded over the last `fadeM` (the brief's "Simple distance-based
 *   shadow culling").
 * - `coral`: a colony's blob its width across times `size`, its height times `lift` the height it is slid by, `alpha`
 *   times the whole's; `plant` the same for an upright limu, `rock` for an outcrop.
 * - `most`: the most the scene's strength (the whole's, a kind's gain, the beam, depth, veil and distance) takes the
 *   ground toward the tint, before each blob's own.
 * - `creature`: a swimming animal's blob its drawn length by its breadth from above (`Caster.width`), its outline
 *   `sizeTimes` its own for legibility and blurred `blurLeastM` plus `blurPerM` of its height over the ground on every
 *   side (M276, the user's report: "they are small circles, not ellipses": a fixed margin of 0.25 m, M263's, had made a
 *   small fish's shadow mostly margin, 0.52 by 0.30 m under the median fish drawn 0.27 m long, 1.7 to 1 against its
 *   body's 5 to 1, MEASURED at the home view at noon; now 0.42 by 0.12 m at the ground, 3.6 to 1, and 0.48 by 0.19 m 0.8 m up, 2.5 to 1 (worked out by `creatureShadows`); JUDGED, the blur a few
 *   degrees' spread of the water's light, as a shadow softens with its caster's height; M253's 0.6 m margin "way too
 *   big", M246's own outline "too small and light"), shrinking by
 *   `shrink` of its size and fading to nothing by `highestM` over the ground (the brief's "Maximum creature shadow
 *   height"), along `fadePower`; its batch `gain` times the whole's strength, drawn to `farM` from the camera and in the
 *   tint times `shade`. Darker, and fading slower as it rises, in M276 (the user's word: "it looks like its missing because
 *   its so light i cant even see it"): `shade` 0.55 to 0.3 and `fadePower` 1 to 0.5 took the floor's luma under the
 *   nearest roi's, 0.8 m up 3.8 m from the camera, down by 27 on average over its shadow's pixels (56 at most), from 16
 *   (39), as much as the taʻape's 0.3 m over the ground had been, MEASURED at the computer's home view zoomed in at noon,
 *   one frame drawn with the shadows and without them; JUDGED by eye. Each blob's own strength `opacity` 0.85 of it, 15%
 *   lighter (M281, the user's word: "make the shadows 15% lighter"): the floor taken 15% less far toward the tint.
 *   Made larger and darker in M253 (the user's word: "the fish shadows are too small and light, i can't really see
 *   them"): MEASURED at the home view at noon, the median fish drawn 0.26 m long, 0.6 m over the ground and 17.7 m
 *   from the camera, so M246's blob was 5 cm wide, a fifth faded there (the stills' `farM`, fading from 16 m), and at
 *   best took the floor to the tint, 101 in luma against the noon floor's 141. Over the computer's home view's shadow
 *   pixels (found by drawing them red; a frame with `fx=no-shadows` beside it) the floor's luma fell by 10 on average
 *   (31 at the 90th percentile) with blobs 2 times their length and 0.8 of that wide at `alpha` 1.6 drawn to 40 m, 16
 *   (40) shaded 0.55, 24 (60) at `alpha` 2.5, and 31 (70) at `gain` 3.5 as set;
 *   the settings themselves JUDGED by eye, on the phone and the computer, at noon, at night (the same as
 *   `fx=no-shadows`) and zoomed in.
 */
export const SHADOWS = {
  alpha: 0.6,
  deepFade: 0.5,
  tint: [0x526c70, 0x304e60],
  tintAtM: [3, 25],
  slideMost: 0.9,
  farM: 22,
  fadeM: 6,
  coral: { size: 1.15, lift: 0.4, alpha: 0.8 },
  plant: { size: 1.1, lift: 0.4, alpha: 0.55 },
  rock: { size: 1.05, lift: 0.3, alpha: 0.7 },
  most: 0.85,
  creature: { sizeTimes: 1.4, blurLeastM: 0.02, blurPerM: 0.05, gain: 3.5, farM: 40, shade: 0.3, highestM: 4, shrink: 0.3, fadePower: 0.5, opacity: 0.85 },
} as const;

/**
 * Whether the page draws the shadows (M246, the brief's "Add graphics-quality parameters where useful"): all but where its
 * address's `fx` words say `no-shadows` (as the particles' `fx`, `particleSettings`), for a phone that cannot spare them
 * and to measure what they cost.
 */
export function shadowsOn(search: string): boolean {
  return !(new URLSearchParams(search).get('fx') ?? '').split(',').map((w) => w.trim()).includes('no-shadows');
}

/** The page's own: its address's. */
export function pageShadowsOn(): boolean {
  return shadowsOn(typeof location === 'undefined' ? '' : location.search);
}

/** The blobs' shapes, each a square of `SHAPE_PX` pixels in the texture's row (M246: four, irregular). */
const SHAPES = 4;
const SHAPE_PX = 16;

/**
 * Each blob's quad cut into this many steps a side, for a draped blob's points to follow the ground (M264): a monk seal's
 * 2.4 m blob 0.3 m a step, near the ground's own quarter meter; a fish's a few centimeters.
 */
export const DRAPE_STEPS = 8;

/**
 * Where a draped blob's four nodes about a point differ by more than this, m, and between them the ground drawn falls
 * more than `DRAPE_FLOAT_M` under the ramp the bilinear rule lays over them (a face: `GroundCache` marks the cell, its
 * texture's green), it is laid on the lowest of them, not on the ramp (M264's review: across a rock's sheer face between
 * two nodes, the bilinear rule ramped the blob up in front of the face into the water); on the rock's top beside the face
 * the lowest lays it inside the rock, hidden. Not on the ground's slopes, which the ramp lies on (M276, the user's
 * report: "they are not present for all fish": the rule had held on every cell whose nodes differed by this, a fifth of
 * the Reef's floor's, MEASURED, 15,553 of its 73,359 quarter-meter cells, and there each blob had sunk under the floor
 * drawn, hidden, the near fish's over the slope below the camera among them; its first remedy, the rule on the outcrops'
 * rock alone, had laid 52% of the draped points over the rock inside it, its review's). MEASURED over the shadows' ground
 * every 7.3 cm, the Reef's nodes on its floor's points (`ReefScene`), lifted 4 cm: 0.49% of the draped points more than
 * 5 mm under the ground drawn, 0.06% more than 10 cm over it (23% and 1.8% of those over the rock), against 20.5% under
 * with M264's rule, its 2 cm and its quarter meter.
 */
export const DRAPE_STEP_M = 0.15;

/** How far a cell's ramp may float over the ground drawn before it is a face, m (`DRAPE_STEP_M`; JUDGED, against 5 cm: 1.2% under). */
export const DRAPE_FLOAT_M = 0.1;

/** One shadow: the ground point under its caster, the ground's normal there, its length and width, the way its length heads (x, z), its height over the ground and its strength, 0 to 1. */
export interface Shadow {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  nz: number;
  length: number;
  width: number;
  headX: number;
  headZ: number;
  lift: number;
  alpha: number;
  /** Its caster's height, m, a swimmer's: a draped blob is laid on no ground above it (M264's review); none for a standing one. */
  top?: number;
  shape: number;
}

/**
 * The blobs' texture: `SHAPES` irregular blobs in a row, each `SHAPE_PX` square, its alpha the blob's darkness in four
 * steps, pixel-art soft (ordered dither between steps) and darkest at its middle. Each shape an ellipse whose edge
 * wanders by the angle's own harmonics, drawn from `hash3` (a drawing's own, never the simulation's).
 */
export function shadowTexture(): THREE.DataTexture {
  const w = SHAPES * SHAPE_PX;
  const data = new Uint8Array(w * SHAPE_PX * 4);
  const bayer = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];
  for (let s = 0; s < SHAPES; s++) {
    const wobble = [1, 2, 3].map((k) => [0.12 * hash3(s, k, 246), 6.283 * hash3(s, k, 247)] as const);
    for (let y = 0; y < SHAPE_PX; y++) {
      for (let x = 0; x < SHAPE_PX; x++) {
        const [u, v] = [((x + 0.5) / SHAPE_PX) * 2 - 1, ((y + 0.5) / SHAPE_PX) * 2 - 1];
        const angle = Math.atan2(v, u);
        const edge = 0.92 + wobble.reduce((a, [amp, phase], k) => a + amp * Math.sin((k + 2) * angle + phase), 0);
        const r = Math.hypot(u, v) / edge;
        // Darkest at its middle, easing to nothing at its edge; stepped in fours, dithered between.
        const dark = Math.max(0, Math.min(1, 1 - r)) ** 0.8;
        const steps = dark * 4 + ((bayer[(y % 4) * 4 + (x % 4)] as number) / 16 - 0.5);
        const level = Math.max(0, Math.min(4, Math.round(steps))) / 4;
        const i = (y * w + s * SHAPE_PX + x) * 4;
        data.set([255, 255, 255, Math.round(255 * Math.min(level, dark > 0 ? 1 : 0))], i);
      }
    }
  }
  const texture = new THREE.DataTexture(data, w, SHAPE_PX, THREE.RGBAFormat);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.needsUpdate = true;
  return texture;
}

const VERTEX = /* glsl */ `
attribute vec3 iAt;
attribute vec3 iNormal;
attribute vec4 iSize;
attribute vec3 iShape;
attribute float iOwn;
uniform vec3 uSun;
uniform float uSlideMost;
uniform sampler2D uGround;
uniform vec4 uGroundAt;
uniform float uDrape;
${WATER_FOG}varying vec2 vUv;
varying float vOwn;
varying vec4 vFog;
varying float vDepth;
varying float vAway;
// The ground's height at a point (x, z), bilinear between the ground cache's nodes as its own heightAt: uGroundAt its first
// node's x and z and its step; the texture its rows of nodes, read at their middles.
float groundAt(vec2 p) {
  vec2 size = vec2(textureSize(uGround, 0));
  vec2 u = clamp((p - uGroundAt.xy) / uGroundAt.z, vec2(0.0), size - 1.0);
  vec2 c = min(floor(u), size - 2.0);
  vec2 f = u - c;
  float h00 = texture2D(uGround, (c + 0.5) / size).r;
  float h10 = texture2D(uGround, (c + vec2(1.5, 0.5)) / size).r;
  float h01 = texture2D(uGround, (c + vec2(0.5, 1.5)) / size).r;
  float h11 = texture2D(uGround, (c + 1.5) / size).r;
  float low = min(min(h00, h10), min(h01, h11));
  // At a face (the cell marked, its first node's green), its foot's, not a ramp (DRAPE_STEP_M).
  if (texture2D(uGround, (c + 0.5) / size).g > 0.5) return low;
  return mix(mix(h00, h10, f.x), mix(h01, h11, f.x), f.y);
}
void main() {
  // The sun's way bent into the water: its slant's sine over the index (Snell's law), its slide per meter of height.
  vec3 sun = normalize(uSun);
  float sinAir = length(sun.xz);
  float sinWater = sinAir / ${REFRACTION.toFixed(2)};
  float slant = min(sinWater / sqrt(max(1.0 - sinWater * sinWater, 1e-4)), uSlideMost);
  vec2 away = sinAir > 1e-4 ? -sun.xz / sinAir : vec2(0.0);
  vec3 n = normalize(iNormal);
  // Its length along its heading laid on the ground's plane, its width across it.
  vec3 head = normalize(vec3(iSize.z, 0.0, iSize.w));
  vec3 along = normalize(head - n * dot(head, n));
  vec3 across = normalize(cross(n, along));
  vec3 at = iAt + vec3(away.x, 0.0, away.y) * slant * iShape.y;
  // Lifted a little off the ground along its normal, so the floor never cuts it.
  at += n * 0.02;
  vec3 corner = at + along * position.x * iSize.x + across * position.y * iSize.y;
  // Draped (M264): each of its points laid on the ground under it, its outline as seen from above, so over a ledge's
  // corner it bends down the rock as the light falls, not on in the water as a plate on the ground's plane at its middle.
  if (uDrape > 0.5) {
    vec3 flatAlong = normalize(head);
    // Across it as the plate's own (n x along with n up), so its triangles keep their winding and face up.
    vec3 flatAcross = vec3(flatAlong.z, 0.0, -flatAlong.x);
    corner = at + flatAlong * position.x * iSize.x + flatAcross * position.y * iSize.y;
    // Never on ground above its caster (iShape.z, its height: M264's review), where the light cannot reach past it: there
    // laid at the caster's height, inside the rock, and hidden by it. Lifted 4 cm (M276; M264's 2 cm left 0.97% of the
    // draped points more than 5 mm under the ground drawn, 0.42% at 4 cm, MEASURED as DRAPE_STEP_M says).
    corner.y = min(groundAt(corner.xz), iShape.z) + 0.04;
  }
  vec4 world = modelMatrix * vec4(corner, 1.0);
  vec4 mv = viewMatrix * world;
  gl_Position = projectionMatrix * mv;
  vFog = waterFog(world.xyz);
  vDepth = -world.y;
  vAway = -mv.z;
  vUv = vec2((iShape.x + uv.x) / ${SHAPES.toFixed(1)}, uv.y);
  vOwn = iOwn;
}
`;

const FRAGMENT = /* glsl */ `
uniform sampler2D map;
uniform float uStrength;
uniform float uDeepFade;
uniform vec3 uTintShallow;
uniform vec3 uTintDeep;
uniform vec2 uTintAt;
uniform vec2 uFar;
varying vec2 vUv;
varying float vOwn;
varying vec4 vFog;
varying float vDepth;
varying float vAway;
void main() {
  float a = texture2D(map, vUv).a;
  if (a * vOwn < 0.01) discard;
  float deep = clamp((vDepth - uTintAt.x) / (uTintAt.y - uTintAt.x), 0.0, 1.0);
  vec3 tint = mix(uTintShallow, uTintDeep, deep);
  // Weaker deep, in the veil, far from the camera, and as the sun's beam is (none by night, M256).
  float strength = uStrength * (1.0 - uDeepFade * clamp(vDepth / 30.0, 0.0, 1.0)) * (1.0 - vFog.a) * (1.0 - smoothstep(uFar.x - uFar.y, uFar.x, vAway));
  // The scene's strength held under the most (review M253's: a near swimmer's past one had clamped its blob's every
  // step to a flat plate), then its own, so its steps stay graded and it still fades as its caster rises or fades in
  // (the second review's: held after its own, a fish 2 m up cast as dark as one at the ground).
  gl_FragColor = vec4(tint, a * vOwn * min(strength, ${SHADOWS.most.toFixed(2)}));
  #include <colorspace_fragment>
}
`;

/** The batch's instanced attributes. */
const ATTRIBUTES = ['iAt', 'iNormal', 'iSize', 'iShape', 'iOwn'] as const;

/** One kind's shadows in one instanced draw: `set` for a fixed lot (laid once a day), or `set` each frame for a moving one. */
export class ShadowBatch {
  readonly mesh: THREE.Mesh;
  private readonly geometry = new THREE.InstancedBufferGeometry();
  private readonly material: THREE.ShaderMaterial;
  private capacity = 0;
  private at = new Float32Array(0);
  private normal = new Float32Array(0);
  private size = new Float32Array(0);
  private shape = new Float32Array(0);
  private own = new Float32Array(0);
  private count = 0;

  /**
   * `kind`: how far from the camera its blobs are drawn, how dark its tint and its gain (the swimming's, `SHADOWS.creature`, M253);
   * `steps`, its quad's cuts a side, `DRAPE_STEPS` for a batch to be draped (`drape`), else one (M264's review: every
   * colony's blob had been cut too, 81 points for 4, for nothing).
   */
  constructor(texture: THREE.Texture, private readonly kind: { farM: number; shade: number; gain: number } = { farM: SHADOWS.farM, shade: 1, gain: 1 }, steps = 1) {
    const quad = new THREE.PlaneGeometry(1, 1, steps, steps);
    this.geometry.index = quad.index;
    this.geometry.setAttribute('position', quad.getAttribute('position'));
    this.geometry.setAttribute('uv', quad.getAttribute('uv'));
    const [shallow, deep] = SHADOWS.tint.map((h) => new THREE.Color(h).multiplyScalar(kind.shade));
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        map: { value: texture }, uSun: { value: new THREE.Vector3(0, 1, 0) }, uStrength: { value: 0 }, uSlideMost: { value: SHADOWS.slideMost },
        uDeepFade: { value: SHADOWS.deepFade }, uTintShallow: { value: shallow }, uTintDeep: { value: deep }, uTintAt: { value: new THREE.Vector2(...SHADOWS.tintAtM) },
        uFar: { value: new THREE.Vector2(kind.farM, SHADOWS.fadeM) }, uGround: { value: null }, uGroundAt: { value: new THREE.Vector4() }, uDrape: { value: 0 }, ...noFog(),
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    // After the floor and the rock (opaque, and the rock's 2.9), before the colonies (3) and the fish (4): laid on the
    // ground, under whatever stands on it.
    this.mesh.renderOrder = 2.95;
    this.mesh.frustumCulled = false;
    this.mesh.raycast = () => undefined;
    this.resize(64);
  }

  private resize(capacity: number): void {
    // Its old buffers freed first: three keeps the most instances an instanced geometry may draw from its first binding
    // until it is disposed, so a batch grown without it drew only its first (the farthest) blobs (as `FishBatch` does).
    this.geometry.dispose();
    this.capacity = capacity;
    this.at = new Float32Array(capacity * 3);
    this.normal = new Float32Array(capacity * 3);
    this.size = new Float32Array(capacity * 4);
    this.shape = new Float32Array(capacity * 3);
    this.own = new Float32Array(capacity);
    const make = (array: Float32Array, width: number): THREE.InstancedBufferAttribute => new THREE.InstancedBufferAttribute(array, width).setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute('iAt', make(this.at, 3));
    this.geometry.setAttribute('iNormal', make(this.normal, 3));
    this.geometry.setAttribute('iSize', make(this.size, 4));
    this.geometry.setAttribute('iShape', make(this.shape, 3));
    this.geometry.setAttribute('iOwn', make(this.own, 1));
  }

  /** The shadows to draw, the first `count` of them (all unless said), replacing the last. */
  set(shadows: readonly Shadow[], count = shadows.length): void {
    if (count > this.capacity) this.resize(Math.max(count, this.capacity * 2));
    for (let i = 0; i < count; i++) {
      const s = shadows[i] as Shadow;
      const [i3, i4] = [i * 3, i * 4];
      this.at[i3] = s.x;
      this.at[i3 + 1] = s.y;
      this.at[i3 + 2] = s.z;
      this.normal[i3] = s.nx;
      this.normal[i3 + 1] = s.ny;
      this.normal[i3 + 2] = s.nz;
      this.size[i4] = s.length;
      this.size[i4 + 1] = s.width;
      this.size[i4 + 2] = s.headX;
      this.size[i4 + 3] = s.headZ;
      this.shape[i3] = s.shape;
      this.shape[i3 + 1] = s.lift;
      // A standing blob's no caster's height (a float's own large number, never under the ground).
      this.shape[i3 + 2] = s.top ?? 1e6;
      this.own[i] = s.alpha;
    }
    this.count = count;
    this.geometry.instanceCount = count;
    for (const name of ATTRIBUTES) {
      const a = this.geometry.getAttribute(name) as THREE.InstancedBufferAttribute;
      a.needsUpdate = true;
      a.clearUpdateRanges();
      a.addUpdateRange(0, count * a.itemSize);
    }
  }

  /** Its blobs laid on the ground's own heights, each point on the ground under it (M264): the ground's texture, `GroundCache.texture`. */
  drape(ground: GroundCache): void {
    const u = this.material.uniforms;
    (u.uGround as { value: THREE.Texture | null }).value = ground.texture();
    (u.uGroundAt as { value: THREE.Vector4 }).value.set(...ground.origin());
    (u.uDrape as { value: number }).value = 1;
  }

  /** How many it draws, for the tests. */
  drawn(): number {
    return this.count;
  }

  /** The sun's way (toward it, scene space) and its strength, 0 to 1: the hour's (`lightAt`'s key), none by the moon (M256). */
  setSun(direction: THREE.Vector3, strength: number): void {
    (this.material.uniforms.uSun?.value as THREE.Vector3).copy(direction);
    (this.material.uniforms.uStrength as { value: number }).value = SHADOWS.alpha * this.kind.gain * strength;
  }

  /** The water the shadows are seen through (`HabitatWater.fog`). */
  fogUniforms(): Partial<FogUniforms> {
    return this.material.uniforms as unknown as Partial<FogUniforms>;
  }

  /** The shader's own strength now, for the tests. */
  strength(): number {
    return (this.material.uniforms.uStrength as { value: number }).value;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

/**
 * The ground's height and its normal, read from a grid of the ground's heights read once as it is made (the brief's
 * "Cached terrain height queries"; the ground never moves once the scene is laid: MEASURED, the Reef's 231 by 501
 * nodes 0.2 m apart and their faces, 75 ms in Node, the median of seven; 23 ms at M246's quarter meter, none marked): `stepM` apart, bilinear between; each cell whose nodes differ by more than
 * `DRAPE_STEP_M` and whose ramp floats more than `DRAPE_FLOAT_M` over the ground at any of its inner points, four by four,
 * marked a face (M276).
 */
export class GroundCache {
  private readonly cols: number;
  private readonly rows: number;
  private readonly heights: Float32Array;
  private readonly face: Uint8Array;
  private heightTexture: THREE.DataTexture | null = null;

  constructor(groundAt: (x: number, z: number) => number, private readonly x0: number, private readonly z0: number, x1: number, z1: number, private readonly stepM: number) {
    this.cols = Math.ceil((x1 - x0) / stepM) + 1;
    this.rows = Math.ceil((z1 - z0) / stepM) + 1;
    this.heights = new Float32Array(this.cols * this.rows);
    this.face = new Uint8Array(this.cols * this.rows);
    for (let r = 0; r < this.rows; r++) for (let c = 0; c < this.cols; c++) this.heights[r * this.cols + c] = groundAt(x0 + c * stepM, z0 + r * stepM);
    for (let r = 0; r < this.rows - 1; r++) {
      for (let c = 0; c < this.cols - 1; c++) {
        const h = [this.node(c, r), this.node(c + 1, r), this.node(c, r + 1), this.node(c + 1, r + 1)] as const;
        if (Math.max(...h) - Math.min(...h) <= DRAPE_STEP_M) continue;
        let floats = false;
        for (let i = 1; i < 5 && !floats; i++) {
          for (let j = 1; j < 5 && !floats; j++) {
            const [fu, fv] = [i / 5, j / 5];
            const ramp = (h[0] * (1 - fu) + h[1] * fu) * (1 - fv) + (h[2] * (1 - fu) + h[3] * fu) * fv;
            floats = ramp - groundAt(x0 + (c + fu) * stepM, z0 + (r + fv) * stepM) > DRAPE_FLOAT_M;
          }
        }
        this.face[r * this.cols + c] = floats ? 1 : 0;
      }
    }
  }

  private node(c: number, r: number): number {
    return this.heights[Math.min(this.rows - 1, Math.max(0, r)) * this.cols + Math.min(this.cols - 1, Math.max(0, c))] as number;
  }

  /** The ground's height at a point, m. */
  heightAt(x: number, z: number): number {
    // Scalars, not destructured pairs (review 32's: a pair an array each call, six calls a swimming shadow a frame).
    const u = (x - this.x0) / this.stepM;
    const v = (z - this.z0) / this.stepM;
    const c = Math.floor(u);
    const r = Math.floor(v);
    const fu = u - c;
    const fv = v - r;
    return (this.node(c, r) * (1 - fu) + this.node(c + 1, r) * fu) * (1 - fv) + (this.node(c, r + 1) * (1 - fu) + this.node(c + 1, r + 1) * fu) * fv;
  }

  /**
   * The ground's height at a point as the drape's shader reads it (M269): a face's cell its lowest node, else as
   * `heightAt`; its cell held within the grid as the shader's is. So a swimming shadow is culled, faded and slid by the
   * ground it is laid on (review 30's: a fish 0.3 m over the sand a quarter meter from a 1 m face had read the ramp half
   * a meter up and lost its shadow, which the shader would have laid on the sand at the face's foot).
   */
  drapedAt(x: number, z: number): number {
    // Scalars, as `heightAt`'s (review 33's: pairs and the four heights an array each call, three calls a shadow a frame).
    const u = Math.min(Math.max((x - this.x0) / this.stepM, 0), this.cols - 1);
    const v = Math.min(Math.max((z - this.z0) / this.stepM, 0), this.rows - 1);
    const c = Math.min(Math.floor(u), this.cols - 2);
    const r = Math.min(Math.floor(v), this.rows - 2);
    const h0 = this.node(c, r);
    const h1 = this.node(c + 1, r);
    const h2 = this.node(c, r + 1);
    const h3 = this.node(c + 1, r + 1);
    if (this.face[r * this.cols + c] === 1) return Math.min(h0, h1, h2, h3);
    const fu = u - c;
    const fv = v - r;
    return (h0 * (1 - fu) + h1 * fu) * (1 - fv) + (h2 * (1 - fu) + h3 * fu) * fv;
  }

  /**
   * The ground a caster stands over, for its shadow's slide (M269's review): at a face, its nearest node's, the face's
   * side it is on, not the foot's (`drapedAt`), so a fish over the rock's lip slides by its height over the rock, not by
   * the step's whole height more; else as `heightAt`.
   */
  standsOver(x: number, z: number): number {
    const u = Math.min(Math.max((x - this.x0) / this.stepM, 0), this.cols - 1);
    const v = Math.min(Math.max((z - this.z0) / this.stepM, 0), this.rows - 1);
    const c = Math.min(Math.floor(u), this.cols - 2);
    const r = Math.min(Math.floor(v), this.rows - 2);
    if (this.face[r * this.cols + c] === 1) return this.node(Math.round(u), Math.round(v));
    return this.heightAt(x, z);
  }

  /** Every node's height as a texture, its rows along z, for the shader to read (M264), its green 1 where a cell is a face, at its first node (M276): made on the first call, kept after. */
  texture(): THREE.DataTexture {
    if (this.heightTexture) return this.heightTexture;
    const data = new Float32Array(this.heights.length * 2);
    for (let i = 0; i < this.heights.length; i++) [data[2 * i], data[2 * i + 1]] = [this.heights[i] as number, this.face[i] as number];
    const texture = new THREE.DataTexture(data, this.cols, this.rows, THREE.RGFormat, THREE.FloatType);
    texture.magFilter = THREE.NearestFilter;
    texture.minFilter = THREE.NearestFilter;
    texture.needsUpdate = true;
    this.heightTexture = texture;
    return texture;
  }

  /** The first node's x and z and the step: the shader's `uGroundAt`. */
  origin(): [number, number, number, number] {
    return [this.x0, this.z0, this.stepM, 0];
  }

  dispose(): void {
    this.heightTexture?.dispose();
  }

  /** The ground's normal at a point, from its heights a step either way. */
  normalAt(x: number, z: number, out: THREE.Vector3): THREE.Vector3 {
    const s = this.stepM;
    const dx = (this.heightAt(x + s, z) - this.heightAt(x - s, z)) / (2 * s);
    const dz = (this.heightAt(x, z + s) - this.heightAt(x, z - s)) / (2 * s);
    return out.set(-dx, 1, -dz).normalize();
  }
}

/** A swimming caster: its body's middle, its drawn length and its breadth from above over its length, and the way it heads (x, z). */
export interface Caster {
  x: number;
  y: number;
  z: number;
  length: number;
  width: number;
  headX: number;
  headZ: number;
  /** How much of it is drawn, 0 to 1 (faded in or out), its shadow as much. */
  alpha: number;
}

/**
 * How far a shadow slides from under its caster per meter of the caster's height, x and z: away from the sun, by the
 * light's slant once bent into the water (Snell's law, `REFRACTION`), at most `SHADOWS.slideMost` (the shader's own rule,
 * for a standing shadow; the swimming's slid here, so their ground is read where they land).
 */
export function slideOf(sun: THREE.Vector3): [number, number] {
  const len = sun.length();
  const [sx, sz] = len > 0 ? [sun.x / len, sun.z / len] : [0, 0];
  const sinAir = Math.hypot(sx, sz);
  if (sinAir < 1e-4) return [0, 0];
  const sinWater = sinAir / REFRACTION;
  const slant = Math.min(sinWater / Math.sqrt(Math.max(1 - sinWater * sinWater, 1e-4)), SHADOWS.slideMost);
  return [(-sx / sinAir) * slant, (-sz / sinAir) * slant];
}

const groundNormal = new THREE.Vector3();

/**
 * The shadows of swimming casters over the ground (`SHADOWS.creature`), into `out`'s own objects, kept from frame to
 * frame (review M246's: an object each a frame), its count returned: each slid away from the sun by its height (`slide`,
 * `slideOf`) and laid on the ground where it lands, that ground's height and normal (review M246's: slid in the shader
 * on the ground under its caster, over a knoll's flank it had hung in the water or sunk under the rock); smaller and
 * lighter as its caster stands higher over that ground, none from `highestM` up, none where the ground there stands over
 * its caster (the rock between it and the light).
 */
export function creatureShadows(lists: readonly (readonly Caster[])[], ground: GroundCache, slide: readonly [number, number], out: Shadow[], along: readonly [number, number] | null = null): number {
  const c = SHADOWS.creature;
  let k = 0;
  for (const casters of lists) {
    for (const f of casters) {
      const under = f.y - ground.drapedAt(f.x, f.z);
      if (!(under > -0.05) || under >= c.highestM) continue;
      const rise = Math.max(0, f.y - ground.standsOver(f.x, f.z));
      const x = f.x + slide[0] * rise;
      const z = f.z + slide[1] * rise;
      const y = ground.drapedAt(x, z);
      const lift = f.y - y;
      if (!(lift > -0.05) || lift >= c.highestM) continue;
      const t = Math.max(0, lift) / c.highestM;
      const scale = 1 - c.shrink * t;
      // Along the sprite as it is drawn, side on to the camera, where `along` gives its way (M263, the user's report: "very
      // shaky and stuttering": laid along each fish's own way from frame to frame, its swimming's wobble had turned it
      // about 3° a frame at the median, under a sprite that never turns); else the caster's own heading.
      const hx = along ? along[0] : f.headX;
      const hz = along ? along[1] : f.headZ;
      const heading = Math.hypot(hx, hz);
      ground.normalAt(x, z, groundNormal);
      const s = out[k] ?? (out[k] = { x: 0, y: 0, z: 0, nx: 0, ny: 1, nz: 0, length: 0, width: 0, headX: 1, headZ: 0, lift: 0, alpha: 0, shape: 0 });
      s.x = x;
      s.y = y;
      s.z = z;
      s.nx = groundNormal.x;
      s.ny = groundNormal.y;
      s.nz = groundNormal.z;
      // Its own outline, `sizeTimes` it for legibility, and blurred by its height (M276): a fish near the floor an ellipse of
      // its own shape, one higher up softer and rounder.
      const blur = c.blurLeastM + c.blurPerM * Math.max(0, lift);
      s.length = f.length * c.sizeTimes * scale + 2 * blur;
      s.width = f.length * f.width * c.sizeTimes * scale + 2 * blur;
      s.headX = heading > 1e-6 ? hx / heading : 1;
      s.headZ = heading > 1e-6 ? hz / heading : 0;
      // Slid already: none in the shader.
      s.lift = 0;
      s.top = f.y;
      s.alpha = f.alpha * c.opacity * (1 - t) ** c.fadePower;
      k++;
    }
  }
  return k;
}

/** A standing caster's shadow (a colony, a plant, a rock): its foot, its width and height, its kind's settings. */
export function standingShadow(x: number, y: number, z: number, normal: THREE.Vector3, width: number, height: number, kind: { size: number; lift: number; alpha: number }): Shadow {
  const turn = 6.283 * hash3(Math.round(x * 100), Math.round(z * 100), 248);
  return {
    x, y, z, nx: normal.x, ny: normal.y, nz: normal.z, length: width * kind.size, width: width * kind.size * (0.8 + 0.2 * hash3(Math.round(x * 100), Math.round(z * 100), 249)),
    headX: Math.cos(turn), headZ: Math.sin(turn), lift: height * kind.lift, alpha: kind.alpha, shape: Math.floor(hash3(Math.round(x * 100), Math.round(z * 100), 250) * SHAPES),
  };
}
